import { AudioManager } from '../../audio/AudioManager'
import { NeonColors, neonButton, neonText, SCREEN_BASE_STYLE, fadeTransition, neonPanel, FONTS } from '../neon-theme'
import {
  GAME_MODE_KEY,
  type GameMode,
  sanitizeGameMode,
  SNAKE_WIDTH_SCALE_KEY,
  CAMERA_ZOOM_SCALE_KEY,
  RENDER_QUALITY_KEY,
} from '../../config/gameMode'
import { isDesktopFrameMode, mountToAppShell } from '../appShell'

const SENSITIVITY_KEY = 'star-snake-sensitivity'
const TUNING_EVENT_NAME = 'star-snake-tuning-updated'

function loadSensitivity(): number {
  const stored = localStorage.getItem(SENSITIVITY_KEY)
  if (stored !== null) {
    const value = Number.parseInt(stored, 10)
    if (!Number.isNaN(value) && value >= 10 && value <= 100) return value
  }
  return 50
}

function saveSensitivity(value: number): void {
  localStorage.setItem(SENSITIVITY_KEY, String(value))
}

function loadNumberSetting(key: string, fallback: number, min: number, max: number): number {
  const stored = localStorage.getItem(key)
  if (stored !== null) {
    const value = Number.parseFloat(stored)
    if (!Number.isNaN(value) && value >= min && value <= max) return value
  }
  return fallback
}

function saveNumberSetting(key: string, value: number): void {
  localStorage.setItem(key, String(value))
}

function loadGameMode(): GameMode {
  return sanitizeGameMode(localStorage.getItem(GAME_MODE_KEY))
}

function saveGameMode(mode: GameMode): void {
  localStorage.setItem(GAME_MODE_KEY, mode)
}

export class SettingsScreen {
  private container: HTMLDivElement
  private onClose: (() => void) | null = null

  constructor() {
    this.container = document.createElement('div')
    this.container.id = 'settings-screen'
    this.container.style.cssText = `
      ${SCREEN_BASE_STYLE}
      background: ${NeonColors.bgOverlay};
      z-index: 210;
      opacity: 0;
      ${fadeTransition(300)}
    `
  }

  show(onClose: () => void): void {
    this.onClose = onClose

    const audio = AudioManager.getInstance()
    const sfxVol = Math.round(audio.getSfxVolume() * 100)
    const musicVol = Math.round(audio.getMusicVolume() * 100)
    const muted = audio.isMuted()
    const sensitivity = loadSensitivity()
    const gameMode = loadGameMode()
    const snakeWidthScale = loadNumberSetting(SNAKE_WIDTH_SCALE_KEY, gameMode === 'classic' ? 1.35 : 1.0, 0.8, 1.8)
    const cameraZoomScale = loadNumberSetting(CAMERA_ZOOM_SCALE_KEY, gameMode === 'classic' ? 1.2 : 1.0, 0.8, 1.5)
    const allowCameraZoomTuning = !isDesktopFrameMode()

    this.container.innerHTML = ''

    const panel = document.createElement('div')
    panel.style.cssText = `
      max-width: 440px; width: 92%;
      max-height: min(92%, 900px);
      overflow-y: auto;
      ${neonPanel()}
      z-index: 30;
    `

    const header = document.createElement('div')
    header.style.cssText = 'display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem;'

    const titleEl = document.createElement('h2')
    titleEl.style.cssText = `
      font-size: 1.5rem; margin: 0; letter-spacing: 0.15em;
      ${neonText(NeonColors.primary, NeonColors.primaryGlow)}
      font-family: ${FONTS.title};
    `
    titleEl.textContent = 'SETTINGS'
    header.appendChild(titleEl)

    const closeBtn = document.createElement('button')
    closeBtn.id = 'settings-close'
    closeBtn.style.cssText = `
      background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
      color: ${NeonColors.textMuted}; border-radius: 50%;
      width: 36px; height: 36px; font-size: 1.2rem;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: border-color 0.2s, color 0.2s, background 0.2s;
    `
    closeBtn.textContent = '\u2715'
    header.appendChild(closeBtn)
    panel.appendChild(header)

    panel.appendChild(this.createSliderSection('SFX Volume', sfxVol, 'sfx-slider', 'sfx-val'))
    panel.appendChild(this.createSliderSection('Music Volume', musicVol, 'music-slider', 'music-val'))
    panel.appendChild(this.createSliderSection('Control Sensitivity', sensitivity, 'sensitivity-slider', 'sensitivity-val', 10, 100))

    const modelSection = document.createElement('div')
    modelSection.style.cssText = 'margin-bottom: 1.2rem;'

    const modelLabel = document.createElement('label')
    modelLabel.style.cssText = `display: block; color: ${NeonColors.textMuted}; font-size: 0.8rem; margin-bottom: 8px;`
    modelLabel.textContent = 'Movement Model'
    modelSection.appendChild(modelLabel)

    const modelSelect = document.createElement('select')
    modelSelect.id = 'movement-model-select'
    modelSelect.style.cssText = `
      width: 100%;
      background: rgba(0,2,10,0.85);
      color: ${NeonColors.textBright};
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 12px;
      padding: 10px 12px;
      font-size: 0.8rem;
      font-family: ${FONTS.body};
    `
    modelSelect.innerHTML = `
      <option value="modern">Modern</option>
      <option value="classic">Classic</option>
    `
    modelSelect.value = gameMode
    modelSection.appendChild(modelSelect)

    const modelHint = document.createElement('div')
    modelHint.textContent = 'Game mode now changes the intended control/visual preset only. Physics and net sync are fixed by the server.'
    modelHint.style.cssText = `color: ${NeonColors.textDim}; font-size: 0.72rem; margin-top: 7px; line-height: 1.35;`
    modelSection.appendChild(modelHint)
    panel.appendChild(modelSection)

    const visualsHeader = document.createElement('div')
    visualsHeader.textContent = 'VISUALS'
    visualsHeader.style.cssText = `
      ${neonText(NeonColors.primary, NeonColors.primaryGlow)}
      font-size: 0.9rem; font-weight: bold;
      margin: 1.5rem 0 0.7rem; text-align: center;
      letter-spacing: 0.15em; font-family: ${FONTS.title};
    `
    panel.appendChild(visualsHeader)

    panel.appendChild(this.createNumberSliderSection(
      'Snake Width Scale', snakeWidthScale, 'snake-width-slider', 'snake-width-val', 0.8, 1.8, 0.05,
      (value) => value.toFixed(2), NeonColors.primary,
    ))
    if (allowCameraZoomTuning) {
      panel.appendChild(this.createNumberSliderSection(
        'Camera Zoom Scale', cameraZoomScale, 'camera-zoom-slider', 'camera-zoom-val', 0.8, 1.5, 0.05,
        (value) => value.toFixed(2), NeonColors.primary,
      ))
    }

    // --- Graphics Quality Section ---
    const graphicsHeader = document.createElement('div')
    graphicsHeader.textContent = 'GRAPHICS'
    graphicsHeader.style.cssText = `
      ${neonText(NeonColors.primary, NeonColors.primaryGlow)}
      font-size: 0.9rem; font-weight: bold;
      margin: 1.5rem 0 0.7rem; text-align: center;
      letter-spacing: 0.15em; font-family: ${FONTS.title};
    `
    panel.appendChild(graphicsHeader)

    const qualitySection = document.createElement('div')
    qualitySection.style.cssText = 'margin-bottom: 1.2rem;'

    const qualityLabel = document.createElement('label')
    qualityLabel.style.cssText = `display: block; color: ${NeonColors.textMuted}; font-size: 0.8rem; margin-bottom: 8px;`
    qualityLabel.textContent = 'Render Quality'
    qualitySection.appendChild(qualityLabel)

    const qualitySelect = document.createElement('select')
    qualitySelect.id = 'render-quality-select'
    qualitySelect.style.cssText = `
      width: 100%;
      background: rgba(0,2,10,0.85);
      color: ${NeonColors.textBright};
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 12px;
      padding: 10px 12px;
      font-size: 0.8rem;
      font-family: ${FONTS.body};
    `
    qualitySelect.innerHTML = `
      <option value="0">Low</option>
      <option value="1">Medium</option>
      <option value="2">High</option>
    `
    const currentQuality = localStorage.getItem(RENDER_QUALITY_KEY) ?? '1'
    qualitySelect.value = currentQuality
    qualitySection.appendChild(qualitySelect)

    const qualityHint = document.createElement('div')
    qualityHint.textContent = 'Higher quality uses more GPU. Requires page reload to take effect.'
    qualityHint.style.cssText = `color: ${NeonColors.textDim}; font-size: 0.72rem; margin-top: 7px; line-height: 1.35;`
    qualitySection.appendChild(qualityHint)
    panel.appendChild(qualitySection)

    const safetyHint = document.createElement('div')
    safetyHint.textContent = allowCameraZoomTuning
      ? 'Movement, sync and network tuning were removed from the production UI to keep everyone on the same authoritative model.'
      : 'Desktop uses a locked camera framing so browser size and local zoom settings do not expand your field of view.'
    safetyHint.style.cssText = `color: ${NeonColors.textDim}; font-size: 0.72rem; margin: 0.8rem 0 1rem; text-align: center; line-height: 1.35;`
    panel.appendChild(safetyHint)

    const muteRow = document.createElement('div')
    muteRow.style.cssText = `
      display: flex; align-items: center; justify-content: space-between;
      padding: 12px 0; border-top: 1px solid rgba(255,255,255,0.06);
    `

    const muteLabel = document.createElement('span')
    muteLabel.style.cssText = `color: ${NeonColors.textMuted}; font-size: 0.85rem;`
    muteLabel.textContent = 'Mute All'
    muteRow.appendChild(muteLabel)

    const toggleWrap = document.createElement('label')
    toggleWrap.style.cssText = 'position: relative; display: inline-block; width: 48px; height: 26px;'

    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.id = 'mute-toggle'
    checkbox.checked = muted
    checkbox.style.cssText = 'opacity: 0; width: 0; height: 0;'
    toggleWrap.appendChild(checkbox)

    const toggleTrack = document.createElement('span')
    toggleTrack.id = 'mute-slider'
    toggleTrack.style.cssText = `
      position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
      background: ${muted ? NeonColors.primary : NeonColors.textDim};
      border-radius: 26px; transition: background 0.2s;
      ${muted ? `box-shadow: 0 0 8px ${NeonColors.primaryGlow};` : ''}
    `

    const toggleKnob = document.createElement('span')
    toggleKnob.style.cssText = `
      position: absolute; height: 20px; width: 20px;
      left: ${muted ? '24px' : '3px'}; top: 3px;
      background: white; border-radius: 50%;
      transition: left 0.2s;
    `
    toggleTrack.appendChild(toggleKnob)
    toggleWrap.appendChild(toggleTrack)
    muteRow.appendChild(toggleWrap)
    panel.appendChild(muteRow)

    const closePrimaryBtn = document.createElement('button')
    closePrimaryBtn.id = 'settings-close-primary'
    closePrimaryBtn.textContent = 'DONE'
    closePrimaryBtn.style.cssText = `
      ${neonButton(NeonColors.primary, NeonColors.primaryGlow)}
      width: 100%; margin-top: 1rem;
    `
    panel.appendChild(closePrimaryBtn)

    this.container.appendChild(panel)
    mountToAppShell(this.container)
    this.bindEvents()

    requestAnimationFrame(() => { this.container.style.opacity = '1' })
  }

  private createSliderSection(label: string, value: number, sliderId: string, valId: string, min: number = 0, max: number = 100): HTMLDivElement {
    const section = document.createElement('div')
    section.style.cssText = 'margin-bottom: 1.5rem;'

    const labelEl = document.createElement('label')
    labelEl.style.cssText = `display: block; color: ${NeonColors.textMuted}; font-size: 0.85rem; margin-bottom: 8px;`
    labelEl.innerHTML = `${label}: <span id="${valId}" style="color: ${NeonColors.primary};">${value}%</span>`
    section.appendChild(labelEl)

    const slider = document.createElement('input')
    slider.type = 'range'
    slider.id = sliderId
    slider.min = String(min)
    slider.max = String(max)
    slider.value = String(value)
    slider.style.cssText = `width: 100%; accent-color: ${NeonColors.primary};`
    section.appendChild(slider)

    return section
  }

  private createNumberSliderSection(
    label: string, value: number, sliderId: string, valId: string,
    min: number, max: number, step: number,
    formatter: (value: number) => string, accentColor: string,
  ): HTMLDivElement {
    const section = document.createElement('div')
    section.style.cssText = 'margin-bottom: 1.2rem;'

    const labelEl = document.createElement('label')
    labelEl.style.cssText = `display: block; color: ${NeonColors.textMuted}; font-size: 0.8rem; margin-bottom: 8px;`
    labelEl.innerHTML = `${label}: <span id="${valId}" style="color: ${NeonColors.primary};">${formatter(value)}</span>`
    section.appendChild(labelEl)

    const slider = document.createElement('input')
    slider.type = 'range'
    slider.id = sliderId
    slider.min = String(min)
    slider.max = String(max)
    slider.step = String(step)
    slider.value = String(value)
    slider.style.cssText = `width: 100%; accent-color: ${accentColor};`
    section.appendChild(slider)

    return section
  }

  private bindNumberSlider(
    sliderId: string, valId: string, key: string,
    min: number, max: number, formatter: (value: number) => string,
  ): void {
    const slider = this.container.querySelector(`#${sliderId}`) as HTMLInputElement | null
    const valueEl = this.container.querySelector(`#${valId}`)
    slider?.addEventListener('input', () => {
      const raw = Number.parseFloat(slider.value)
      if (!Number.isFinite(raw)) return
      const clamped = Math.max(min, Math.min(max, raw))
      if (valueEl) valueEl.textContent = formatter(clamped)
      saveNumberSetting(key, clamped)
      this.emitTuningChanged()
    })
  }

  private emitTuningChanged(): void {
    window.dispatchEvent(new CustomEvent(TUNING_EVENT_NAME))
  }

  private bindEvents(): void {
    const close = () => { this.onClose?.(); this.hide() }

    this.container.querySelector('#settings-close')?.addEventListener('click', close)
    this.container.querySelector('#settings-close-primary')?.addEventListener('click', close)

    const sfxSlider = this.container.querySelector('#sfx-slider') as HTMLInputElement | null
    const sfxVal = this.container.querySelector('#sfx-val')
    sfxSlider?.addEventListener('input', () => {
      const volume = Number.parseInt(sfxSlider.value, 10)
      if (sfxVal) sfxVal.textContent = `${volume}%`
      AudioManager.getInstance().setSfxVolume(volume / 100)
    })

    const musicSlider = this.container.querySelector('#music-slider') as HTMLInputElement | null
    const musicVal = this.container.querySelector('#music-val')
    musicSlider?.addEventListener('input', () => {
      const volume = Number.parseInt(musicSlider.value, 10)
      if (musicVal) musicVal.textContent = `${volume}%`
      AudioManager.getInstance().setMusicVolume(volume / 100)
    })

    const sensitivitySlider = this.container.querySelector('#sensitivity-slider') as HTMLInputElement | null
    const sensitivityVal = this.container.querySelector('#sensitivity-val')
    sensitivitySlider?.addEventListener('input', () => {
      const value = Number.parseInt(sensitivitySlider.value, 10)
      if (sensitivityVal) sensitivityVal.textContent = `${value}%`
      saveSensitivity(value)
      this.emitTuningChanged()
    })

    const modelSelect = this.container.querySelector('#movement-model-select') as HTMLSelectElement | null
    modelSelect?.addEventListener('change', () => {
      saveGameMode(sanitizeGameMode(modelSelect.value))
      this.emitTuningChanged()
    })

    const qualitySelect = this.container.querySelector('#render-quality-select') as HTMLSelectElement | null
    qualitySelect?.addEventListener('change', () => {
      localStorage.setItem(RENDER_QUALITY_KEY, qualitySelect.value)
    })

    this.bindNumberSlider('snake-width-slider', 'snake-width-val', SNAKE_WIDTH_SCALE_KEY, 0.8, 1.8, (value) => value.toFixed(2))
    if (!isDesktopFrameMode()) {
      this.bindNumberSlider('camera-zoom-slider', 'camera-zoom-val', CAMERA_ZOOM_SCALE_KEY, 0.8, 1.5, (value) => value.toFixed(2))
    }

    const muteToggle = this.container.querySelector('#mute-toggle') as HTMLInputElement | null
    const muteSlider = this.container.querySelector('#mute-slider') as HTMLElement | null
    muteToggle?.addEventListener('change', () => {
      const isMuted = muteToggle.checked
      AudioManager.getInstance().setMuted(isMuted)
      if (muteSlider) {
        muteSlider.style.background = isMuted ? NeonColors.primary : NeonColors.textDim
        muteSlider.style.boxShadow = isMuted ? `0 0 8px ${NeonColors.primaryGlow}` : 'none'
        const knob = muteSlider.querySelector('span') as HTMLElement | null
        if (knob) knob.style.left = isMuted ? '24px' : '3px'
      }
    })
  }

  hide(): void {
    this.container.style.opacity = '0'
    setTimeout(() => { this.container.remove() }, 300)
  }

  dispose(): void {
    this.container.remove()
    this.onClose = null
  }
}
